import React, { Component } from "react";
import moment from "moment";
import {
    Table,
    Space,
    Button,
    Tag,
    Avatar,
    Tooltip,
    Input,
    Select,
    DatePicker,
    Row,
    Col,
    Popconfirm,
    Empty,
    Typography,
    Badge,
} from "antd";
import {
    PlayCircleOutlined,
    DeleteOutlined,
    DownloadOutlined,
    SearchOutlined,
    FilterOutlined,
    SortAscendingOutlined,
    SortDescendingOutlined,
    ClockCircleOutlined,
    UserOutlined,
    RocketOutlined,
    CalendarOutlined,
    StarOutlined,
    UploadOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import "./ReplayList.scss";

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

class ReplayList extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showFilters: false,
            tableLoading: false,
            localFilters: {
                search: "",
                cast: "",
                dateRange: null,
            },
            pagination: {
                current: 1,
                pageSize: 10,
                total: 0,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} replays`,
                pageSizeOptions: ["5", "10", "20", "50"],
                size: "small",
            },
        };
    }

    componentDidMount() {
        // Initialize pagination from props if available
        if (this.props.total) {
            this.setState((prevState) => ({
                pagination: {
                    ...prevState.pagination,
                    total: this.props.total,
                },
            }));
        }
    }

    componentDidUpdate(prevProps) {
        // Update pagination total when props change
        if (prevProps.total !== this.props.total) {
            this.setState((prevState) => ({
                pagination: {
                    ...prevState.pagination,
                    total: this.props.total || 0,
                },
            }));
        }
    }

    handleRefresh = () => {
        this.setState({ tableLoading: true });

        // Call parent component's refresh method if available
        if (this.props.onRefresh) {
            this.props.onRefresh();
        } else if (this.props.onFilterChange) {
            // Fallback: reload current data with existing filters
            this.props.onFilterChange(
                this.props.filterOptions,
                this.state.pagination.current,
                this.state.pagination.pageSize
            );
        }

        // Reset loading state after a short delay
        setTimeout(() => {
            this.setState({ tableLoading: false });
        }, 500);
    };

    getStatusTag = (record) => {
        if (!record) return;
        const threeMonthsAgo = moment().subtract(3, "months");
        const isOlderThan3Months = moment(record.createdAt).isBefore(
            threeMonthsAgo
        );
        let status = isOlderThan3Months ? "expired" : "processed";

        const statusColors = {
            processed: "success",
            expired: "warning",
            failed: "error",
        };

        const statusTexts = {
            processed: "Ready",
            expired: "Expired",
            failed: "Failed",
        };

        return (
            <Tag color={statusColors[status]} className="status-tag">
                {statusTexts[status]}
            </Tag>
        );
    };

    handleSearch = (value) => {
        const newFilters = {
            ...this.props.filterOptions,
            search: value,
        };
        this.setState((prevState) => ({
            localFilters: {
                ...prevState.localFilters,
                search: value,
            },
            pagination: {
                ...prevState.pagination,
                current: 1, // Reset to first page when searching
            },
        }));
        this.props.onFilterChange(
            newFilters,
            1,
            this.state.pagination.pageSize
        );
    };

    handleCastSearch = (value) => {
        const newFilters = {
            ...this.props.filterOptions,
            cast: value,
        };
        this.setState((prevState) => ({
            localFilters: {
                ...prevState.localFilters,
                cast: value,
            },
            pagination: {
                ...prevState.pagination,
                current: 1, // Reset to first page when filtering
            },
        }));
        this.props.onFilterChange(
            newFilters,
            1,
            this.state.pagination.pageSize
        );
    };

    handleDateRangeChange = (dates) => {
        const newFilters = {
            ...this.props.filterOptions,
            startDate: dates && dates[0] ? dates[0] : null,
            endDate: dates && dates[1] ? dates[1] : null,
        };
        this.setState((prevState) => ({
            localFilters: {
                ...prevState.localFilters,
                dateRange: dates,
            },
            pagination: {
                ...prevState.pagination,
                current: 1, // Reset to first page when filtering
            },
        }));
        this.props.onFilterChange(
            newFilters,
            1,
            this.state.pagination.pageSize
        );
    };

    handleClearFilters = () => {
        const clearedFilters = {
            search: "",
            cast: "",
            startDate: null,
            endDate: null,
        };
        this.setState({
            localFilters: {
                search: "",
                cast: "",
                dateRange: null,
            },
            pagination: {
                ...this.state.pagination,
                current: 1, // Reset to first page when clearing filters
            },
        });
        this.props.onFilterChange(
            clearedFilters,
            1,
            this.state.pagination.pageSize
        );
    };

    handleDownload = (replay) => {
        console.log("Downloading replay:", replay.id);
        // Implement download logic here
    };

    handleTableChange = (pagination, filters, sorter) => {
        console.log("Table change:", { pagination, filters, sorter });

        const { current, pageSize } = pagination;

        this.setState((prevState) => ({
            pagination: {
                ...prevState.pagination,
                current: current,
                pageSize: pageSize,
            },
        }));

        // Call parent component to load new data
        if (this.props.onFilterChange) {
            this.props.onFilterChange(
                this.props.filterOptions,
                current,
                pageSize
            );
        }

        // Handle sorting if provided
        if (sorter && sorter.field && this.props.onSortChange) {
            const order = sorter.order === "ascend" ? "asc" : "desc";
            this.props.onSortChange(sorter.field, order);
        }
    };

    getColumns = () => [
        {
            title: "Replay",
            dataIndex: "title",
            key: "title",
            width: 250,
            render: (text, record) => (
                <div className="replay-info">
                    <div className="replay-thumbnail">
                        <Avatar
                            src={record.image}
                            size={48}
                            shape="square"
                            icon={<PlayCircleOutlined />}
                            className="thumbnail-avatar"
                        />
                        {/* {record.highlights > 0 && (
							<Badge 
								count={record.highlights} 
								size="small"
								className="highlights-badge"
								style={{ backgroundColor: '#f59e0b' }}
							/>
						)} */}
                    </div>
                    <div className="replay-details">
                        <div className="replay-title" title={text}>
                            {text}
                        </div>
                        <div className="replay-meta">
                            <Space
                                size={4}
                                split={<span className="separator">•</span>}
                            >
                                {record.cast?.map((castName, index) => (
                                    <span key={index} className="meta-item">
                                        <UserOutlined />
                                        {castName}
                                    </span>
                                ))}
                            </Space>
                        </div>
                    </div>
                </div>
            ),
            ellipsis: true,
        },
        {
            title: "Duration",
            dataIndex: "duration",
            key: "duration",
            width: 100,
            render: (_, record) => {
                const calculateDuration = (startTime, endTime) => {
                    if (!startTime || !endTime) return "00:00:00";

                    const start = new Date(startTime);
                    const end = new Date(endTime);
                    const diffMs = end - start;

                    if (diffMs < 0) return "00:00:00";

                    const hours = Math.floor(diffMs / (1000 * 60 * 60));
                    const minutes = Math.floor(
                        (diffMs % (1000 * 60 * 60)) / (1000 * 60)
                    );
                    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

                    return `${hours.toString().padStart(2, "0")}:${minutes
                        .toString()
                        .padStart(2, "0")}:${seconds
                        .toString()
                        .padStart(2, "0")}`;
                };

                const duration = calculateDuration(
                    record.liveStartTime,
                    record.liveEndTime
                );

                return (
                    <Space size={4}>
                        <ClockCircleOutlined />
                        <Text type="secondary">{duration}</Text>
                    </Space>
                );
            },
        },
        // {
        // 	title: 'Size',
        // 	dataIndex: 'size',
        // 	key: 'size',
        // 	width: 80,
        // 	render: (size) => (
        // 		<Text type="secondary">{size}</Text>
        // 	),
        // 	sorter: (a, b) => {
        // 		const sizeToBytes = (size) => {
        // 			const units = { 'B': 1, 'KB': 1024, 'MB': 1024*1024, 'GB': 1024*1024*1024 };
        // 			const match = size.match(/^([\d.]+)\s*([A-Z]+)$/);
        // 			return match ? parseFloat(match[1]) * units[match[2]] : 0;
        // 		};
        // 		return sizeToBytes(a.size) - sizeToBytes(b.size);
        // 	}
        // },
        {
            title: "Date",
            dataIndex: "liveStartTime",
            key: "liveStartTime",
            width: 110,
            render: (date) => (
                <Space size={4}>
                    <CalendarOutlined />
                    <Text type="secondary">
                        {date ? moment(date).format("YYYY-MM-DD HH:mm") : "-"}
                    </Text>
                </Space>
            ),
            // sorter: true, // Enable server-side sorting
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 100,
            render: (status, record) => this.getStatusTag(record),
        },
        // {
        // 	title: 'Viewers',
        // 	dataIndex: 'viewers',
        // 	key: 'viewers',
        // 	width: 100,
        // 	render: (viewers) => (
        // 		<Space size={4}>
        // 			<EyeOutlined />
        // 			<Text type="secondary">{viewers?.toLocaleString()}</Text>
        // 		</Space>
        // 	),
        // 	sorter: (a, b) => (a.viewers || 0) - (b.viewers || 0)
        // },
        {
            title: "Actions",
            key: "actions",
            width: 140,
            fixed: "right",
            render: (_, record) => (
                <Space size="small" className="action-buttons">
                    <Tooltip title="Play Replay">
                        <Button
                            type="text"
                            size="small"
                            icon={<PlayCircleOutlined />}
                            onClick={() => this.props.onReplaySelect(record)}
                            disabled={record.status !== "processed"}
                            className="action-btn play-btn"
                        />
                    </Tooltip>
                    {/* <Tooltip title="Download">
						<Button
							type="text"
							size="small"
							icon={<DownloadOutlined />}
							onClick={() => this.handleDownload(record)}
							disabled={record.status !== 'processed'}
							className="action-btn download-btn"
						/>
					</Tooltip> */}
                    <Popconfirm
                        title="Delete Replay"
                        description="Are you sure you want to delete this replay? This action cannot be undone."
                        onConfirm={() =>
                            this.props.onReplayDelete(record.contentId)
                        }
                        okText="Delete"
                        cancelText="Cancel"
                        okType="danger"
                        placement="topRight"
                    >
                        <Tooltip title="Delete">
                            <Button
                                type="text"
                                size="small"
                                icon={<DeleteOutlined />}
                                danger
                                className="action-btn delete-btn"
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    renderFilters = () => {
        const { localFilters } = this.state;

        return (
            <div className="replay-filters">
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6}>
                        <Search
                            placeholder="Search replays..."
                            allowClear
                            value={localFilters.search}
                            onChange={(e) =>
                                this.setState((prevState) => ({
                                    localFilters: {
                                        ...prevState.localFilters,
                                        search: e.target.value,
                                    },
                                }))
                            }
                            onSearch={this.handleSearch}
                            prefix={<SearchOutlined />}
                            className="search-input"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Search
                            placeholder="Filter by cast name..."
                            allowClear
                            value={localFilters.cast}
                            onChange={(e) =>
                                this.setState((prevState) => ({
                                    localFilters: {
                                        ...prevState.localFilters,
                                        cast: e.target.value,
                                    },
                                }))
                            }
                            onSearch={this.handleCastSearch}
                            prefix={<UserOutlined />}
                            className="filter-select"
                        />
                    </Col>
                    {/* <Col xs={24} sm={12} md={6}>
						<Select
							placeholder="All Games"
							allowClear
							style={{ width: '100%' }}
							value={filterOptions.game}
							onChange={(value) => onFilterChange({ ...filterOptions, game: value })}
							className="filter-select"
						>
							{games.map(game => (
								<Option key={game} value={game}>
									<RocketOutlined /> {game}
								</Option>
							))}
						</Select>
					</Col> */}
                    <Col xs={24} sm={12} md={6}>
                        <RangePicker
                            style={{ width: "100%" }}
                            value={localFilters.dateRange}
                            onChange={this.handleDateRangeChange}
                            placeholder={["Start Date", "End Date"]}
                            className="date-picker"
                        />
                    </Col>
                </Row>

                <div className="filter-actions">
                    <Space>
                        <Button
                            size="small"
                            onClick={this.handleClearFilters}
                            disabled={
                                !localFilters.search &&
                                !localFilters.cast &&
                                !localFilters.dateRange
                            }
                        >
                            Clear Filters
                        </Button>
                        <Text type="secondary" className="results-count">
                            {this.props.total || 0} replays
                        </Text>
                    </Space>
                </div>
            </div>
        );
    };

    render() {
        const { selectedReplay, loading, sortBy, sortOrder, replays } =
            this.props;
        const { showFilters, tableLoading, pagination } = this.state;

        return (
            <div className="replay-list">
                <div className="replay-list-header">
                    <Space>
                        <Tooltip title="Refresh">
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={this.handleRefresh}
                                loading={tableLoading}
                                className="refresh-btn"
                            >
                                Refresh
                            </Button>
                        </Tooltip>
                        <Button
                            icon={<FilterOutlined />}
                            onClick={() =>
                                this.setState({ showFilters: !showFilters })
                            }
                            type={showFilters ? "primary" : "default"}
                            className="filter-toggle"
                        >
                            Filters
                        </Button>
                        {showFilters && (
                            <>
                                <Button
                                    icon={
                                        sortOrder === "asc" ? (
                                            <SortAscendingOutlined />
                                        ) : (
                                            <SortDescendingOutlined />
                                        )
                                    }
                                    onClick={() =>
                                        this.props.onSortChange(
                                            sortBy,
                                            sortOrder === "asc" ? "desc" : "asc"
                                        )
                                    }
                                    size="small"
                                >
                                    Sort by {sortBy}
                                </Button>
                            </>
                        )}
                    </Space>
                </div>

                {showFilters && this.renderFilters()}

                <div className="table-container">
                    <Table
                        columns={this.getColumns()}
                        dataSource={replays}
                        rowKey="contentId"
                        loading={loading || tableLoading}
                        pagination={{
                            ...pagination,
                            total: this.props.total || 0,
                        }}
                        rowSelection={{
                            type: "radio",
                            selectedRowKeys: selectedReplay
                                ? [selectedReplay.contentId]
                                : [],
                            onSelect: (record) =>
                                this.props.onReplaySelect(record),
                            columnWidth: 50,
                        }}
                        rowClassName={(record) => {
                            let className = "replay-row";
                            if (
                                selectedReplay?.contentId === record.contentId
                            ) {
                                className += " selected-row";
                            }
                            if (record.status === "processing") {
                                className += " processing-row";
                            }
                            // if (record.highlights > 5) {
                            // 	className += ' featured-row';
                            // }
                            return className;
                        }}
                        size="small"
                        onChange={this.handleTableChange}
                        locale={{
                            emptyText: (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description="No replays found"
                                    className="empty-state"
                                >
                                    {/* <Button 
										type="primary" 
										icon={<UploadOutlined />}
										onClick={() => {}}
									>
										Upload First Replay
									</Button> */}
                                </Empty>
                            ),
                        }}
                        onRow={(record) => ({
                            onDoubleClick: () => {
                                if (record.status === "processed") {
                                    this.props.onReplaySelect(record);
                                }
                            },
                            className:
                                record.status !== "processed"
                                    ? "disabled-row"
                                    : "",
                        })}
                    />
                </div>
            </div>
        );
    }
}

export default ReplayList;
